"use client";

import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";

interface BeforeInstallPromptEvent extends Event {
	prompt: () => Promise<void>;
	userChoice: Promise<{ outcome: "accepted" | "dismissed"; platform: string }>;
}

export function PWAInstallPrompt() {
	const [isOpen, setIsOpen] = useState(false);
	const [deferredPrompt, setDeferredPrompt] =
		useState<BeforeInstallPromptEvent | null>(null);
	const [isIOS, setIsIOS] = useState(false);
	const [isStandalone, setIsStandalone] = useState(false);
	const [hasUserDismissed, setHasUserDismissed] = useState(false);

	useEffect(() => {
		// --- Temporarily disabled ---
		return;
		// Check if already shown or dismissed in this session
		const hasPromptBeenShown = localStorage.getItem("pwaPromptShown");
		if (hasPromptBeenShown) {
			return;
		}

		// Check if running as standalone PWA already
		if (
			window.matchMedia("(display-mode: standalone)").matches ||
			(window.navigator as any).standalone === true
		) {
			setIsStandalone(true);
			return;
		}

		// Detect iOS
		const isIOSDevice =
			/iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream;
		setIsIOS(isIOSDevice);

		// For non-iOS devices, listen for beforeinstallprompt
		if (!isIOSDevice) {
			const handleBeforeInstallPrompt = (e: Event) => {
				e.preventDefault();
				setDeferredPrompt(e as BeforeInstallPromptEvent);

				// Show our custom prompt after a delay
				setTimeout(() => {
					setIsOpen(true);
				}, 3000);
			};

			window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);

			return () => {
				window.removeEventListener(
					"beforeinstallprompt",
					handleBeforeInstallPrompt,
				);
			};
		} else {
			// For iOS, show our custom prompt after a delay if not already installed
			setTimeout(() => {
				setIsOpen(true);
			}, 3000);
		}
	}, []);

	const handleInstall = async () => {
		if (deferredPrompt) {
			await deferredPrompt.prompt();
			const choiceResult = await deferredPrompt.userChoice;

			if (choiceResult.outcome === "accepted") {
				console.log("User accepted the install prompt");
			} else {
				console.log("User dismissed the install prompt");
			}

			setDeferredPrompt(null);
		}

		setIsOpen(false);
		localStorage.setItem("pwaPromptShown", "true");
	};

	const handleDismiss = () => {
		setIsOpen(false);
		setHasUserDismissed(true);
		localStorage.setItem("pwaPromptShown", "true");
	};

	// Don't show if already installed or user dismissed
	if (isStandalone || hasUserDismissed) {
		return null;
	}
	const APP_NAME = "BRIK";

	return (
		<Dialog open={isOpen} onOpenChange={setIsOpen}>
			<DialogContent className="sm:max-w-md">
				<DialogHeader>
					<DialogTitle>Installer {APP_NAME}</DialogTitle>
				</DialogHeader>

				<div className="flex items-center space-x-2 py-4">
					{isIOS ? (
						<div className="space-y-2">
							{/* === FRENCH TEXT START === */}
							<p>Pour installer {APP_NAME} sur votre appareil iOS :</p>
							<ol className="list-decimal pl-5 space-y-2">
								<li>
									Appuyez sur le bouton de partage{" "}
									<span className="inline-block">⎋</span> en bas de votre
									navigateur.
								</li>
								<li>
									Faites défiler vers le bas et appuyez sur "Ajouter à l'écran
									d'accueil" <span className="inline-block">➕</span>.
								</li>
								<li>Appuyez sur "Ajouter" en haut à droite.</li>
							</ol>
							{/* === FRENCH TEXT END === */}
						</div>
					) : (
						// === FRENCH TEXT START ===
						<p>
							Installez {APP_NAME} pour une expérience plus rapide et réactive !
						</p>
						// === FRENCH TEXT END ===
					)}
				</div>

				<DialogFooter className="flex justify-between sm:justify-between">
					<Button variant="outline" onClick={handleDismiss}>
						Plus tard
					</Button>

					{!isIOS && (
						<Button onClick={handleInstall}>Installer maintenant</Button>
					)}

					{isIOS && <Button onClick={handleDismiss}>Compris</Button>}
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}
